/**
 * Person data processing utilities for kojitsukai people pages
 */

/**
 * Extract birth and death years from years string
 * @param {string} yearsString - Format like "1522–1591", "1522", "?–1603", "600–400 BCE"
 * @returns {Object} Object with birthYear and deathYear
 */
export function extractBirthDeathYears(yearsString) {
  if (!yearsString) return { birthYear: undefined, deathYear: undefined };

  const isBCE = yearsString.includes('BCE');
  const multiplier = isBCE ? -1 : 1;

  // Remove BCE and clean up the string
  const cleanYears = yearsString.replace(/\s*BCE\s*/g, '').trim();

  // Handle different formats
  if (cleanYears.includes('–') || cleanYears.includes('-')) {
    const separator = cleanYears.includes('–') ? '–' : '-';
    const [birth, death] = cleanYears.split(separator).map(s => s.trim());

    return {
      birthYear: birth === '?' ? undefined : parseInt(birth) * multiplier,
      deathYear: death === '?' ? undefined : parseInt(death) * multiplier
    };
  } else {
    // Single year
    const year = cleanYears === '?' ? undefined : parseInt(cleanYears) * multiplier;
    return {
      birthYear: year,
      deathYear: undefined
    };
  }
}

/**
 * Generate a unique ID for a person
 * @param {Object} person - Person data object
 * @returns {string} Unique ID
 */
function generatePersonId(person) {
  const nameToUse = person.name_romaji || '';
  const baseId = nameToUse
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const yearsString = person.years || '';
  const yearMatch = yearsString.match(/\d{3,4}/);
  const yearSuffix = yearMatch ? `-${yearMatch[0]}` : '';
  return `${baseId}${yearSuffix}`;
}

/**
 * Translate person name to English
 * @param {string} nameRomaji - Romanized name
 * @returns {string} English name
 */
function translatePersonName(nameRomaji) {
  // Simple translation mapping - can be expanded
  const translations = {
    'Sen no Rikyū': 'Sen no Rikyū',
    'Sen Sōtan': 'Sen Sōtan',
    'Toyotomi Hideyoshi': 'Toyotomi Hideyoshi',
    'Oda Uraku': 'Oda Uraku',
    'Kobori Enshū': 'Kobori Enshū',
    'Masuda Donnō': 'Masuda Donnō',
    'Ii Naosuke': 'Ii Naosuke'
  };

  return translations[nameRomaji] || nameRomaji;
}

/**
 * Create a basic English biography from Japanese description
 * @param {string} japaneseDescription - Japanese description
 * @param {Object} person - Person data for context
 * @returns {string} English biography
 */
function createEnglishBiography(japaneseDescription, person) {
  // This is a simplified translation - in production would use proper translation service
  const commonTranslations = {
    '哲学者': 'philosopher',
    '茶人': 'tea master',
    '実業家': 'businessman',
    '大名': 'daimyo',
    '武将': 'military commander',
    '陶芸家': 'potter',
    '職人': 'craftsman'
  };

  let englishBio = `${translatePersonName(person.name_romaji || '')} (${person.years || ''}) was a historical figure `;

  // Add basic context based on connections and items
  const connections = Array.isArray(person.connections) ? person.connections : [];
  const items = Array.isArray(person.items) ? person.items : [];
  const description = (japaneseDescription || person.description || '');

  if (connections.some(conn => conn.includes('茶')) ||
      items.some(item => item.includes('茶')) ||
      description.includes('茶')) {
    englishBio += 'who was a tea master associated with the Japanese tea ceremony. ';
  } else if (connections.some(conn => conn.includes('織田') || conn.includes('豊臣'))) {
    englishBio += 'from the Sengoku period. ';
  } else {
    englishBio += 'from Japanese history. ';
  }

  return englishBio + 'More details available from historical sources.';
}

/**
 * Transform raw person data into detailed person object
 * @param {Object} person - Raw person data
 * @param {Object} itemsData - Items data for building relationships
 * @returns {Object} Transformed person details
 */
export function transformPersonData(person, itemsData = {}, itemSlugs = {}) {
  const { birthYear, deathYear } = extractBirthDeathYears(person.years);
  const personId = generatePersonId(person);

  // Build related items from the person's items list
  const relatedItems = (person.items || []).map(itemName => {
    const itemConnections = itemsData[itemName] || [];
    const personConnection = itemConnections.find(conn =>
      conn.person.includes(person.name_jp) || conn.person.includes(person.name_romaji)
    );

    return {
      id: itemSlugs[itemName] || itemName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-'),
      name: {
        jp: itemName,
        en: translateItemName(itemName)
      },
      type: classifyItemType(itemName),
      relationship: personConnection?.connection || 'associated with'
    };
  });

  return {
    ...person,
    id: personId,
    birthYear,
    deathYear,
    biography: {
      jp: person.description,
      en: createEnglishBiography(person.description, person)
    },
    relatedItems,
    relatedPeople: [], // Will be populated by cross-reference
    familyMembers: [] // Will be populated by family relationship analysis
  };
}

/**
 * Simple item name translation
 * @param {string} itemName - Japanese item name
 * @returns {string} English item name
 */
function translateItemName(itemName) {
  const translations = {
    '茶碗': 'Tea bowl',
    '茶杓': 'Tea scoop',
    '花入': 'Flower vase',
    '香合': 'Incense container',
    '水指': 'Water jar',
    '釜': 'Kettle',
    '掛物': 'Hanging scroll',
    '羽箒': 'Feather brush',
    '竹': 'bamboo',
    '尺八切': 'shakuhachi cut'
  };

  for (const [jp, en] of Object.entries(translations)) {
    if (itemName.includes(jp)) {
      return itemName.replace(jp, en);
    }
  }

  return itemName; // Return original if no translation found
}

/**
 * Simple item type classification
 * @param {string} itemName - Item name
 * @returns {string} Item type
 */
function classifyItemType(itemName) {
  if (itemName.includes('茶碗')) return 'tea_bowl';
  if (itemName.includes('茶杓')) return 'tea_scoop';
  if (itemName.includes('花入')) return 'flower_vase';
  if (itemName.includes('香合')) return 'incense_container';
  if (itemName.includes('水指')) return 'water_jar';
  if (itemName.includes('釜') || itemName.includes('風炉')) return 'kettle';
  if (itemName.includes('掛物') || itemName.includes('掛軸')) return 'scroll';
  return 'other';
}

/**
 * Build family connections from person data
 * @param {Object} person - Person data
 * @param {Array} allPersons - All persons data for cross-reference
 * @returns {Array} Array of family members
 */
export function buildFamilyConnections(person, allPersons) {
  const familyMembers = [];

  person.connections.forEach(connection => {
    // Look for family relationship indicators
    if (connection.includes('(祖父)')) {
      const memberName = connection.replace(/\s*\(祖父\)/, '');
      const member = allPersons.find(p =>
        p.name_jp === memberName || p.name_romaji === memberName
      );

      if (member) {
        familyMembers.push({
          id: generatePersonId(member),
          name: {
            jp: member.name_jp,
            en: translatePersonName(member.name_romaji)
          },
          relationship: 'ancestor',
          years: member.years
        });
      }
    }

    // Add more family relationship patterns as needed
    const familyKeywords = ['父', '母', '息子', '娘', '兄', '弟', '妻', '夫'];
    familyKeywords.forEach(keyword => {
      if (connection.includes(keyword)) {
        // Handle family relationship
      }
    });
  });

  return familyMembers;
}

/**
 * Create a person summary object
 * @param {Object} person - Person data
 * @param {string} relationship - Optional relationship description
 * @returns {Object} Person summary
 */
export function createPersonSummary(person, relationship = undefined) {
  return {
    id: generatePersonId(person),
    name: {
      jp: person.name_jp,
      en: translatePersonName(person.name_romaji)
    },
    years: person.years,
    relationship
  };
}

/**
 * Validate person data structure
 * @param {Object} person - Person data to validate
 * @returns {Object} Validation result
 */
export function validatePersonData(person) {
  const errors = [];

  const requiredFields = ['name_jp', 'name_romaji', 'years', 'region', 'description'];

  requiredFields.forEach(field => {
    if (!person[field]) {
      errors.push({
        field,
        message: `${field} is required`,
        value: person[field]
      });
    }
  });

  // Validate years format
  if (person.years && !isValidYearsFormat(person.years)) {
    errors.push({
      field: 'years',
      message: 'Invalid years format. Expected format: "1522–1591" or "1522" or "?–1603"',
      value: person.years
    });
  }

  // Validate arrays
  if (person.connections && !Array.isArray(person.connections)) {
    errors.push({
      field: 'connections',
      message: 'connections must be an array',
      value: person.connections
    });
  }

  if (person.items && !Array.isArray(person.items)) {
    errors.push({
      field: 'items',
      message: 'items must be an array',
      value: person.items
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Check if years format is valid
 * @param {string} years - Years string to validate
 * @returns {boolean} True if valid format
 */
function isValidYearsFormat(years) {
  const patterns = [
    /^\d{3,4}–\d{3,4}$/,        // 1522–1591
    /^\d{3,4}$/,                // 1522
    /^\?–\d{3,4}$/,             // ?–1603
    /^\d{3,4}–\?$/,             // 1522–?
    /^\d{3,4}–\d{3,4}\s+BCE$/,  // 600–400 BCE
    /^江戸末期–明治初期$/          // Special period format
  ];

  return patterns.some(pattern => pattern.test(years));
}